import { FC } from 'react';

import { ChartBarIcon } from '@heroicons/react/outline';
import { WeakPasswordBadge } from '@shared/components/WeakPasswordBadge';
import { useAuth } from '@shared/state/hooks/useAuth';
import { LowerRole } from '@shared/types';
import { Link, useLocation, useParams } from 'react-router-dom';

import { Icon } from './icons';

const linkColorGenerator = (isAccent: boolean) =>
  isAccent ? 'theme-bg-main theme-text-4' : 'theme-text-main';

// 답례품 섹션도 상단 메뉴처럼 선택 시 브랜드 색으로 꽉 채운다(theme-bg-main).
// 단, 일러스트 아이콘은 색이 박혀있어 어두운 배경에 묻히므로, 선택 시 아이콘만
// 흰색 칩(giftIconChip) 위에 올려 또렷하게 보이도록 한다.
const giftLinkClass = (isAccent: boolean) =>
  `flex items-center space-x-2.5 rounded-md px-2 py-1.5 ${
    isAccent ? 'theme-bg-main theme-text-4' : 'theme-text-main'
  }`;

const giftSubLinkClass = (isAccent: boolean) =>
  `flex items-center space-x-2 rounded-md px-2 py-1 ${
    isAccent ? 'theme-bg-main theme-text-4' : 'theme-text-main'
  }`;

// 선택된 줄에서 아이콘을 흰 배경 칩 위에 올려 색이 묻히지 않게 한다.
const giftIconChip = (isAccent: boolean) =>
  `flex items-center justify-center rounded-md p-1 ${isAccent ? 'bg-white' : ''}`;

const StatPageSideBar: FC = () => {
  const { bugoBrandId } = useParams();
  const { pathname, search } = useLocation();

  // 통합 정산 페이지의 현재 탭 (?tab=) — 하위 메뉴(배송형·쿠폰형) 강조에 쓴다.
  // 탭 파라미터가 없으면 '월별 합계'라 하위 메뉴는 어느 쪽도 강조하지 않는다.
  const giftSettlementTab = pathname.includes('/gift-settlement')
    ? new URLSearchParams(search).get('tab')
    : null;

  const { userProfile, bugoRole, userBugoBrand, isBugoAgencyAdmin, isAdmin } = useAuth();

  const {
    WreathBig,
    BugoMessage,
    Receipt,
    Register,
    Delivery,
    MoneyBag,
    CashAccount,
    Time,
    User,
    ChevronRight,
  } = Icon();
  const showGiftEventLog = isBugoAgencyAdmin() || isAdmin();

  const bugoBrand = userBugoBrand;

  return (
    <aside className="sticky top-0 h-screen w-60 shrink-0 overflow-y-auto bg-white p-3">
      {/* 회사 / 유저 정보 */}
      <div className="space-y-1.5 border-b border-gray-300 pt-1 pb-3 font-bold">
        <h6 className="px-2 text-xs text-gray-500">{bugoBrand?.bugoAgency?.corpName}</h6>
        {/* 회사/역할 블록을 누르면 내 계정(이름·연락처·비밀번호)으로 간다 */}
        <Link
          to={`/bugo-brand/${bugoBrandId}/settings`}
          className="flex items-center justify-between gap-2 rounded-md bg-[#F3F6FE] px-3 py-2 theme-text-main"
        >
          <h4>
            {bugoRole() === LowerRole.BugoAgencyBranchAdmin
              ? userProfile?.bugoAgencyBranchAdminDetail?.teamName
              : bugoBrand?.bugoAgency?.nickName}{' '}
            관리자
          </h4>
          <ChevronRight />
        </Link>
      </div>
      {/* 버튼 리스트 */}
      <div className="space-y-0.5 py-3 text-xs font-bold">
        <div>
          <Link
            to={`/bugo-brand/${bugoBrandId}/flower-stat`}
            className={`flex items-center space-x-2.5 rounded-md px-2 py-1.5 ${linkColorGenerator(
              pathname.endsWith('flower-stat'),
            )}`}
          >
            <WreathBig className="m-1" />
            <h5>화환 내역</h5>
          </Link>
        </div>
        <div>
          <Link
            to={`/bugo-brand/${bugoBrandId}/bugo-stat`}
            className={`flex items-center space-x-2.5 rounded-md px-2 py-1.5 ${linkColorGenerator(
              pathname.endsWith('bugo-stat'),
            )}`}
          >
            <BugoMessage />
            <h5>부고 발송 내역</h5>
          </Link>
        </div>
        <div>
          <Link
            to={`/bugo-brand/${bugoBrandId}/bugo-stat-new`}
            className={`flex items-center space-x-2.5 rounded-md px-2 py-1.5 ${linkColorGenerator(
              pathname.endsWith('bugo-stat-new'),
            )}`}
          >
            <BugoMessage />
            <h5>부고 발송 내역(발인일)</h5>
          </Link>
        </div>
        <div>
          <Link
            to={`/bugo-brand/${bugoBrandId}/agency-worker-stat`}
            className={`flex items-center space-x-2.5 rounded-md px-2 py-1.5 ${linkColorGenerator(
              pathname.endsWith('agency-worker-stat'),
            )}`}
          >
            <Register />
            <h5>지도사</h5>
          </Link>
        </div>
        <div>
          <Link
            to={`/bugo-brand/${bugoBrandId}/account`}
            className={`flex items-center space-x-2.5 rounded-md px-2 py-1.5 ${linkColorGenerator(
              pathname.endsWith('account'),
            )}`}
          >
            <div className="p-1">
              <Receipt
                className={`wh-6 ${pathname.endsWith('account') ? 'fill-white' : ''}`}
              />
            </div>
            <h5>정산 내역</h5>
          </Link>
        </div>
        <div>
          <Link
            to={`/bugo-brand/${bugoBrandId}/flower-price-table`}
            className={`flex items-center space-x-2.5 rounded-md px-2 py-1.5 ${linkColorGenerator(
              pathname.endsWith('flower-price-table'),
            )}`}
          >
            <WreathBig className="m-1" />
            <h5>화환 단가표</h5>
          </Link>
        </div>
        {showGiftEventLog && (
          <>
            <div className="mt-1.5 border-t border-gray-200 pt-1.5">
              <h6 className="mb-0.5 px-2 text-[10px] font-semibold uppercase tracking-wider text-gray-400">
                답례품
              </h6>
              <Link
                to={`/bugo-brand/${bugoBrandId}/gift-event-log`}
                className={giftLinkClass(pathname.endsWith('gift-event-log'))}
              >
                <div className={giftIconChip(pathname.endsWith('gift-event-log'))}>
                  <Time className="wh-6" />
                </div>
                <h5>답례품 로그</h5>
              </Link>
              <Link
                to={`/bugo-brand/${bugoBrandId}/gift-delivery-management`}
                className={giftLinkClass(pathname.endsWith('gift-delivery-management'))}
              >
                <div
                  className={giftIconChip(pathname.endsWith('gift-delivery-management'))}
                >
                  <Delivery className="wh-6" />
                </div>
                <h5>배송상품 관리</h5>
              </Link>
              <Link
                to={`/bugo-brand/${bugoBrandId}/gift-dashboard`}
                className={giftLinkClass(pathname.endsWith('gift-dashboard'))}
              >
                {/* 수익 대시보드만 currentColor 아이콘 — 흰 칩에 올리면 선택 시 흰색끼리
                    겹쳐 안 보이므로 칩 없이 두고 여백만 맞춘다 */}
                <div className="p-1">
                  <ChartBarIcon className="wh-6" />
                </div>
                <h5>수익 대시보드</h5>
              </Link>
              {/* 정산은 통합 페이지 한 곳 — 종류(배송형·쿠폰형)는 탭(?tab=)으로 진입한다 */}
              <Link
                to={`/bugo-brand/${bugoBrandId}/gift-settlement`}
                className={giftLinkClass(pathname.includes('/gift-settlement'))}
              >
                <div className={giftIconChip(pathname.includes('/gift-settlement'))}>
                  <Receipt className="wh-6" />
                </div>
                <h5>정산 관리</h5>
              </Link>
              <div className="ml-8 space-y-0.5 text-sm font-medium">
                <Link
                  to={`/bugo-brand/${bugoBrandId}/gift-settlement?tab=delivery`}
                  className={giftSubLinkClass(giftSettlementTab === 'delivery')}
                >
                  <div className={giftIconChip(giftSettlementTab === 'delivery')}>
                    <Delivery className="wh-5" />
                  </div>
                  <span>배송형</span>
                </Link>
                <Link
                  to={`/bugo-brand/${bugoBrandId}/gift-settlement?tab=coupon`}
                  className={giftSubLinkClass(giftSettlementTab === 'coupon')}
                >
                  <div className={giftIconChip(giftSettlementTab === 'coupon')}>
                    <CashAccount className="wh-5" />
                  </div>
                  <span>쿠폰형</span>
                </Link>
              </div>
              <Link
                to={`/bugo-brand/${bugoBrandId}/gift-price-table`}
                className={giftLinkClass(pathname.endsWith('gift-price-table'))}
              >
                <div className={giftIconChip(pathname.endsWith('gift-price-table'))}>
                  <MoneyBag className="wh-6" />
                </div>
                <h5>단가표</h5>
              </Link>
            </div>
          </>
        )}
        {/* 계정 관련은 맨 아래 별도 그룹 */}
        <div className="mt-1.5 border-t border-gray-200 pt-1.5">
          <Link
            to={`/bugo-brand/${bugoBrandId}/settings`}
            className={`flex items-center space-x-2.5 rounded-md px-2 py-1.5 ${linkColorGenerator(
              pathname.endsWith('settings'),
            )}`}
          >
            <div className="p-1">
              <User />
            </div>
            <h5>계정정보</h5>
            <WeakPasswordBadge />
          </Link>
        </div>
      </div>
    </aside>
  );
};

export default StatPageSideBar;
